import { FunctionComponent } from "react";
import { toast } from "react-toastify";

import { GetApp } from "@material-ui/icons";
import { ExportFormat, exportFile } from "utils/exportUtils";

import { useUser, useUserEvents } from "hooks";

import useExportProductionVolumesMutation from "api/useExportProductionVolumesMutation";

import { EntityKind } from "models/entityKind";

import { ExportIconButton } from "components/shared/ExportIconButton";

import MonthlyDisabledButton from "./MonthlyDisabledButton";
import useMonthlyProduction from "./hooks/useMonthlyProduction";

const MonthlyExportButton: FunctionComponent = () => {
  const { hasTrialAccount, isReadonly } = useUser();
  const { sendExportDataTableEvent } = useUserEvents();
  const { filter, data, isEmptyFilter, isLoading, isError } = useMonthlyProduction();

  const exportMutation = useExportProductionVolumesMutation(
    (response, params) => exportFile(response.data, params.filename),
    (error) => toast.error(error.message)
  );

  // Hidden for trial and read-only accounts, as the Data view's export is.
  if (isReadonly || hasTrialAccount) return null;

  let disabledReason: string | undefined;
  if (isEmptyFilter || data?.totalWells === 0) {
    disabledReason = "No wells in the current filter.";
  } else if (isError) {
    disabledReason = "Monthly production failed to load.";
  } else if (isLoading || !data) {
    disabledReason = "Monthly production is still loading.";
  }

  if (disabledReason || !data) {
    return (
      <MonthlyDisabledButton
        label="Export Monthly Production"
        reason={disabledReason ?? ""}
        reasonId="monthly-export-disabled-reason">
        <GetApp fontSize="large" />
      </MonthlyDisabledButton>
    );
  }

  return (
    <ExportIconButton
      tooltip="Export Monthly Production"
      loading={exportMutation.isLoading}
      onSelect={(format) => {
        sendExportDataTableEvent({
          format: format === ExportFormat.Excel ? "Excel" : "CSV",
          entityKind: EntityKind.Well,
          wellCount: data.totalWells,
          view: "monthly-production"
        });
        // Over the format's well limit the service answers 400 with the limit, shown as the error.
        exportMutation.mutate({
          request: filter,
          format,
          filename: `EVA Monthly Production.${format}`
        });
      }}
    />
  );
};

export default MonthlyExportButton;
